import { computed, watch } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { locationFacetValue, locationLabel } from '@/composables/records/recordsFormat';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, useSold } from '@/plugins/store/actions/queries/sold';
import { RECORD_ACTIONS_COLUMN_ID, type SoldRow } from '@/types';
import { columnPresence, isColumnAvailable, TOGGLE_COLUMN_IDS, type SoldToggleColumnId } from './useSoldColumnVisibility';

const HEADINGS_KEY = 'views.pigs.records.table.headings';

/** The bulk sale offered above the table: one sale from a barn divided into its sections (the sale form, in bulk). */
export const BULK_CREATE = {
    formId: 5,
    labelKey: 'views.pigs.sold.table.createButton',
} as const;

/** i18n key for each toggleable column's heading, read off the same shared `records` namespace every
 * other records page's headings live under. */
const HEADING_KEYS: Record<SoldToggleColumnId, string> = {
    section: `${HEADINGS_KEY}.section`,
    customer: `${HEADINGS_KEY}.customer`,
    invoice: `${HEADINGS_KEY}.invoice`,
    receiving_chr: `${HEADINGS_KEY}.receivingChr`,
    created_by: `${HEADINGS_KEY}.createdBy`,
};

/**
 * What the Sold page filters on: the single location a departure was registered at. Unlike
 * Insertions/Transfers there is only one end to a departure, so there is no From/To pair here.
 */
function facets(): RecordFacetDefinitions<SoldRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        barn: {
            value: (row) => locationFacetValue(row.farm, row.barn),
            label: (row) => locationLabel(row.farm, row.barn) || blank,
        },
        section: {
            value: (row) => row.section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.section?.name ?? blank,
        },
    };
}

/**
 * The columns, in the order a departure reads: when it happened, where the pigs left from, how many
 * of them at what weight, and - when on - Customer, Invoice, and Receiving CHR.
 *
 * Section, Customer and Invoice are each hard-gated (see `useSoldColumnVisibility`): a non-integrator
 * directory leaves `invoice`/`customer` null on every row, and a departure with no section recorded
 * (farm-level registration) leaves `section` null, so each drops out of the dropdown entirely rather
 * than merely defaulting off - exactly the behaviour this page had before it had any manual toggle at
 * all. Receiving CHR is not gated, and sortable rather than filterable like Customer/Invoice, its
 * nearest neighbours.
 */
function columns(isVisible: (columnId: SoldToggleColumnId) => boolean): ColumnDef<SoldRow>[] {
    const base: ColumnDef<SoldRow>[] = [
        { id: 'date', accessorKey: 'date', header: i18n.global.t('views.pigs.records.table.headings.date'), enableSorting: true },
        { id: 'barn', header: i18n.global.t('views.pigs.records.table.headings.location'), meta: { filterable: true } },
    ];

    if (isVisible('section')) {
        base.push({ id: 'section', header: i18n.global.t(HEADING_KEYS.section), meta: { filterable: true } });
    }

    base.push(
        { id: 'number', accessorKey: 'number', header: i18n.global.t('views.pigs.records.table.headings.pigs'), enableSorting: true, meta: { align: 'right' } },
        { id: 'avg_weight', accessorKey: 'avg_weight', header: i18n.global.t('views.pigs.records.table.headings.avgWeight'), enableSorting: true, meta: { align: 'right' } },
        { id: 'total_weight', accessorKey: 'total_weight', header: i18n.global.t('views.pigs.records.table.headings.totalWeight'), enableSorting: true, meta: { align: 'right' } }
    );

    if (isVisible('customer')) {
        base.push({ id: 'customer', accessorKey: 'customer', header: i18n.global.t(HEADING_KEYS.customer), enableSorting: true });
    }

    // After Customer - Invoice is the one column of the three most likely absent even on an
    // integrator directory (not every departure is invoiced), so it trails the rest.
    if (isVisible('invoice')) {
        base.push({ id: 'invoice', accessorKey: 'invoice', header: i18n.global.t(HEADING_KEYS.invoice), enableSorting: true });
    }

    if (isVisible('receiving_chr')) {
        base.push({ id: 'receiving_chr', accessorKey: 'receiving_chr', header: i18n.global.t(HEADING_KEYS.receiving_chr), enableSorting: true });
    }

    if (isVisible('created_by')) {
        base.push({ id: 'created_by', accessorKey: 'created_by', header: i18n.global.t(HEADING_KEYS.created_by), enableSorting: true });
    }

    return [...base, { id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }];
}

/**
 * The Sold page's configuration of the shared records page, plus the column-visibility control over
 * its four presence-driven columns and Created by.
 *
 * Column visibility resolves in one fixed order - see `useColumnVisibilityOverrides` - availability
 * first (Section/Customer/Invoice only once the rows carry them; Receiving CHR always available),
 * then the reader's own stored choice, else a presence-aware default (Created by is always offered and always defaults off). Both read the whole loaded set
 * (`page.rows`), not the filtered table rows, so a column appears the moment its first non-null value
 * loads and does not flip on/off under the reader just from narrowing the table to one week.
 */
export function useSoldPage() {
    const page = useRecordsPage<SoldRow>({
        records: { use: useSold, rootKey: queryKeys.getSoldRoot },
        facets,
    });

    const presence = computed(() => columnPresence(page.rows.value));

    /**
     * The filterable toggle columns - the only one a stale `columnFilters` entry can affect once its
     * column disappears (Customer/Invoice/Receiving CHR are sortable, never filterable).
     */
    const FILTERABLE_COLUMN_IDS: readonly SoldToggleColumnId[] = ['section'];

    /**
     * Drops a facet's own active filter selection once its column is no longer on the table - a
     * hidden column has no chip left to clear it from, and `applyFacetFilters` would otherwise keep
     * matching rows against a filter the reader can no longer see or reach.
     */
    function clearFilterIfHidden(id: SoldToggleColumnId) {
        if (FILTERABLE_COLUMN_IDS.includes(id) && id in page.columnFilters.value) {
            const next = { ...page.columnFilters.value };

            delete next[id];
            page.columnFilters.value = next;
        }
    }

    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<SoldToggleColumnId>({
        storageKey: 'pigs.sold.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: (id) => isColumnAvailable(id, presence.value),
        defaultVisible: (id) => id !== 'created_by' && presence.value[id],
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
        onHide: clearFilterIfHidden,
    });

    // A period change (or any other change to the loaded set) can drop Section's only data, taking
    // its facet option away with it - not just a user's own toggle.
    watch(presence, () => {
        for (const id of FILTERABLE_COLUMN_IDS) {
            if (!isVisible(id)) {
                clearFilterIfHidden(id);
            }
        }
    });

    // Computed rather than a constant so the headings follow a language switch, and so a column
    // appears the moment its first non-null value loads.
    return { ...page, columns: computed(() => columns(isVisible)), columnToggleOptions, toggleColumn, setAllColumns };
}
